import React from 'react';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';
import { ENTITY_ID_WATER_WASH } from "../generic/Constants";
import { genericGetAll } from "../generic/crud";

type OperationalInsightsCardProps = {
    allClosedJobs: any[];
    allOpenJobs?: any[];
    allActiveJobs?: any[];
    loading?: boolean;
    role: 'manager' | 'supervisor';
    preloadedWaterWashDates?: any[];
};

export const OperationalInsightsCard: React.FC<OperationalInsightsCardProps> = ({
    allClosedJobs,
    allOpenJobs = [],
    allActiveJobs = [],
    loading,
    role,
    preloadedWaterWashDates
}) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
    const [selectedDay, setSelectedDay] = React.useState<{ date: Date, jobs: any[] } | null>(null);
    const [waterWashRecords, setWaterWashRecords] = React.useState<any[]>([]);
    const [waterWashLoading, setWaterWashLoading] = React.useState(!preloadedWaterWashDates);

    // Use preloaded water wash dates if available, otherwise fetch
    React.useEffect(() => {
        if (preloadedWaterWashDates) {
            setWaterWashRecords(preloadedWaterWashDates);
            setWaterWashLoading(false);
            return;
        }
        const fetchWaterWashData = async () => {
            setWaterWashLoading(true);
            try {
                const params = {
                    page: 1,
                    sizePerPage: 5000,
                    currentIndex: 0,
                    sortBy: "createdOn",
                    sortOrder: "desc",
                };
                const response = await genericGetAll(ENTITY_ID_WATER_WASH, params);
                setWaterWashRecords(response.data?.entities || []);
            } catch (error) {
                console.error("Error fetching water wash data:", error);
                setWaterWashRecords([]);
            } finally {
                setWaterWashLoading(false);
            }
        };
        fetchWaterWashData();
    }, [preloadedWaterWashDates]);

    // --- Data Processing Helpers ---
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const last30DaysStart = new Date(today);
    last30DaysStart.setDate(today.getDate() - 30);

    const currentDayOfMonth = new Date().getDate();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const remainingDays = daysInMonth - currentDayOfMonth;

    // Filter Jobs
    const filterJobsByDateRange = (jobs: any[], start: Date, end: Date) => {
        return jobs.filter(job => {
            const jobDate = new Date(job.endDate || job.committedDate);
            jobDate.setHours(0, 0, 0, 0);
            return jobDate >= start && jobDate <= end;
        });
    };

    const last30DaysJobs = filterJobsByDateRange(allClosedJobs, last30DaysStart, today);

    // Day Performance
    const getDayPerformance = (jobs: any[]) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayCounts = new Array(7).fill(0);

        jobs.forEach(job => {
            const date = new Date(job.endDate || job.committedDate);
            const dayIdx = date.getDay();
            dayCounts[dayIdx]++;
        });

        const maxCount = Math.max(...dayCounts);
        const minCount = Math.min(...dayCounts.filter(c => c > 0));

        const bestDayIndex = dayCounts.indexOf(maxCount);
        const slowestDayIndex = dayCounts.indexOf(minCount === Infinity ? 0 : minCount);

        return {
            bestDay: days[bestDayIndex],
            slowestDay: days[slowestDayIndex],
            dayCounts: dayCounts,
            bestCount: maxCount,
            slowestCount: minCount === Infinity ? 0 : minCount
        };
    };

    const dayStats = getDayPerformance(last30DaysJobs);

    // Peak Hours
    const getPeakHour = (jobs: any[]) => {
        const hours = new Array(24).fill(0);
        jobs.forEach(job => {
            if (job.endDate) {
                const hour = new Date(job.endDate).getHours();
                hours[hour]++;
            }
        });
        const maxJobs = Math.max(...hours);
        const peakHour = hours.indexOf(maxJobs);

        const formatHour = (h: number) => {
            const ampm = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            return `${h12} ${ampm}`;
        };
        return maxJobs > 0 ? `${formatHour(peakHour)} - ${formatHour(peakHour + 1)}` : "N/A";
    };

    const peakHour = getPeakHour(last30DaysJobs);

    // Current month stats
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const currentMonthJobs = filterJobsByDateRange(allClosedJobs, currentMonthStart, today);
    const currentMonthJobCount = currentMonthJobs.length;
    const predictedMonthJobCount = Math.round(currentMonthJobCount + ((currentMonthJobCount / currentDayOfMonth) * remainingDays));

    // Comparative Analysis Data
    const todayJobs = filterJobsByDateRange(allClosedJobs, today, new Date(today.getTime() + 24 * 60 * 60 * 1000));

    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(today.getDate() - 7);
    const lastWeekJobs = filterJobsByDateRange(allClosedJobs, lastWeekStart, today);

    const sameDayLastWeek = new Date(today);
    sameDayLastWeek.setDate(today.getDate() - 7);
    const sameDayJobs = filterJobsByDateRange(allClosedJobs, sameDayLastWeek, new Date(sameDayLastWeek.getTime() + 24 * 60 * 60 * 1000));

    const currentActiveJobs = allOpenJobs.length;

    // Generate 20 Operational Recommendations (NO REVENUE)
    const generateOperationalRecommendations = () => {
        const bestDayIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(dayStats.bestDay);
        const slowestDayIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(dayStats.slowestDay);
        const avgDailyJobs = last30DaysJobs.length / 30;
        const dailyJobsNeeded = Math.ceil((predictedMonthJobCount - currentMonthJobCount) / remainingDays);

        const recommendations = [
            // 1. Daily Job Target
            {
                icon: "bi-bullseye",
                color: "#009ef7",
                text: "Daily Job Target",
                subtext: `Complete ${dailyJobsNeeded} jobs/day for the next ${remainingDays} days to hit ${predictedMonthJobCount} jobs this month.`
            },
            // 2. Peak Day Performance
            {
                icon: "bi-calendar-check",
                color: "#50cd89",
                text: "Peak Day Success",
                subtext: `${dayStats.bestDay}s deliver ${dayStats.bestCount} jobs. Replicate this on ${dayStats.slowestDay}s for +${Math.round((dayStats.bestCount - dayStats.slowestCount) * 4.3)} jobs/month.`
            },
            // 3. Job Completion Rate
            {
                icon: "bi-speedometer2",
                color: "#7239ea",
                text: "Completion Velocity",
                subtext: `Current pace: ${Math.round(avgDailyJobs)} jobs/day. Increase to ${Math.round(avgDailyJobs) + 2} jobs/day = ${Math.round((avgDailyJobs + 2) * 30)} monthly jobs.`
            },
            // 4. Peak Hour Optimization
            {
                icon: "bi-clock-fill",
                color: "#ffc700",
                text: "Golden Hour Focus",
                subtext: `${peakHour} is your busiest period. Optimize staffing during this window to handle +2 extra jobs daily.`
            },
            // 5. Slow Day Activation
            {
                icon: "bi-hourglass-split",
                color: "#f1416c",
                text: "Slow Day Boost",
                subtext: `${dayStats.slowestDay}s: ${dayStats.slowestCount} jobs. Special scheduling could increase to ${Math.round(dayStats.slowestCount * 1.5)} jobs = +${Math.round((dayStats.slowestCount * 0.5) * 4.3)} monthly.`
            },
            // 6. Staffing Efficiency
            {
                icon: "bi-people-fill",
                color: "#a8e6cf",
                text: "Team Allocation",
                subtext: `Peak ${dayStats.bestDay}s handle ${dayStats.bestCount} jobs. Ensure ${Math.ceil(dayStats.bestCount / 3)} team members scheduled for optimal flow.`
            },
            // 7. Job Flow Consistency
            {
                icon: "bi-bar-chart-line",
                color: "#4ecdc4",
                text: "Flow Consistency",
                subtext: `Best day completes ${Math.round((dayStats.bestCount / dayStats.slowestCount) * 100)}% more jobs than slowest. Reduce gap by 30% for +${Math.round((dayStats.bestCount - dayStats.slowestCount) * 0.3 * 4.3)} jobs/month.`
            },
            // 8. Weekend Utilization
            {
                icon: "bi-calendar2-week",
                color: "#00a3ff",
                text: "Weekend Capacity",
                subtext: `Weekend jobs: ${dayStats.dayCounts[0] + dayStats.dayCounts[6]}. Promote weekend slots for +${Math.round((dayStats.dayCounts[0] + dayStats.dayCounts[6]) * 0.2 * 4.3)} jobs/month.`
            },
            // 9. Daily Target Progress
            {
                icon: "bi-graph-up",
                color: "#74b9ff",
                text: "Monthly Progress",
                subtext: `${currentMonthJobCount} jobs completed. On track for ${predictedMonthJobCount} jobs by month-end. ${currentMonthJobCount >= predictedMonthJobCount * (currentDayOfMonth / daysInMonth) ? 'Ahead of schedule!' : 'Push harder!'}`
            },
            // 10. Job Backlog Management
            {
                icon: "bi-list-task",
                color: "#ff6b6b",
                text: "Active Jobs",
                subtext: `${allOpenJobs.length} jobs in progress. Target: Complete ${Math.ceil(allOpenJobs.length * 0.4)} today to maintain flow.`
            },
            // 11. Completion Streak
            {
                icon: "bi-fire",
                color: "#ff8b94",
                text: "Momentum Builder",
                subtext: `Last 7 days: ${filterJobsByDateRange(allClosedJobs, new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), today).length} jobs. Maintain this for ${Math.round(filterJobsByDateRange(allClosedJobs, new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), today).length / 7 * 30)} monthly jobs.`
            },
            // 12. Capacity Utilization
            {
                icon: "bi-grid-3x3-gap",
                color: "#ffaaa5",
                text: "Capacity Target",
                subtext: `Best day: ${dayStats.bestCount} jobs. If all days hit 75% (${Math.round(dayStats.bestCount * 0.75)}), that's ${Math.round(dayStats.bestCount * 0.75 * 30)} jobs/month.`
            },
            // 13. Hour-by-Hour Efficiency
            {
                icon: "bi-smartwatch",
                color: "#ffd3b6",
                text: "Hourly Throughput",
                subtext: `Average ${(avgDailyJobs / 8).toFixed(1)} jobs/hour during business hours. Target: ${((avgDailyJobs / 8) * 1.2).toFixed(1)} jobs/hour = +${Math.round(avgDailyJobs * 0.2 * 30)} jobs/month.`
            },
            // 14. Morning vs Evening
            {
                icon: "bi-sun-fill",
                color: "#ffe66d",
                text: "Shift Balance",
                subtext: `Balance morning and evening shifts. Aim for ${Math.round(avgDailyJobs / 2)} jobs per shift for consistent workflow.`
            },
            // 15. Job Type Mix
            {
                icon: "bi-pie-chart-fill",
                color: "#c7ceea",
                text: "Service Distribution",
                subtext: `Diversify job types. Target: 40% quick jobs, 40% standard, 20% complex for optimal scheduling and completion rates.`
            },
            // 16. Team Performance
            {
                icon: "bi-trophy-fill",
                color: "#96ceb4",
                text: "Team Goal",
                subtext: `Team completed ${last30DaysJobs.length} jobs in 30 days. Next target: ${Math.round(last30DaysJobs.length * 1.1)} jobs (+10% growth).`
            },
            // 17. Quality vs Speed
            {
                icon: "bi-shield-check",
                color: "#a29bfe",
                text: "Quality Balance",
                subtext: `Maintain quality while targeting ${Math.round(avgDailyJobs + 1)} jobs/day. Focus on efficient processes, not rushed work.`
            },
            // 18. Resource Planning
            {
                icon: "bi-tools",
                color: "#b8b8ff",
                text: "Resource Readiness",
                subtext: `Plan for ${predictedMonthJobCount} jobs this month. Ensure parts/materials ready for ${Math.ceil(predictedMonthJobCount / daysInMonth)} jobs/day average.`
            },
            // 19. Customer Scheduling
            {
                icon: "bi-calendar3",
                color: "#95e1d3",
                text: "Booking Optimization",
                subtext: `Fill ${dayStats.slowestDay} slots first. Pre-schedule ${Math.round(dayStats.slowestCount * 1.3)} jobs for next ${dayStats.slowestDay} to boost utilization.`
            },
            // 20. Performance Tracking
            {
                icon: "bi-clipboard-data",
                color: "#dfe6e9",
                text: "Daily Scorecard",
                subtext: `Track completions daily. Target: ${Math.ceil(predictedMonthJobCount / daysInMonth)} jobs/day. Current avg: ${Math.round(avgDailyJobs)} jobs/day.`
            }
        ];

        // Randomly select 6 recommendations
        const shuffled = [...recommendations].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, 6);
    };

    const operationalRecommendations = React.useMemo(() => generateOperationalRecommendations(), [allClosedJobs, allOpenJobs]);

    // Generate trend data for sparklines (last 6 weeks)
    const generateWeeklyTrend = (jobs: any[], weeksBack: number = 6) => {
        const weeklyData = [];
        for (let i = weeksBack - 1; i >= 0; i--) {
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - (i * 7));
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 7);

            const weekJobs = filterJobsByDateRange(jobs, weekStart, weekEnd);
            weeklyData.push(weekJobs.length);
        }
        return weeklyData;
    };

    const completedJobsTrend = generateWeeklyTrend(allClosedJobs);
    const activeJobsTrend = [
        allOpenJobs.length * 0.7,
        allOpenJobs.length * 0.8,
        allOpenJobs.length * 0.9,
        allOpenJobs.length * 0.95,
        allOpenJobs.length * 1.0,
        allOpenJobs.length
    ].map(Math.round);
    const avgJobsTrend = completedJobsTrend.map(jobs => Math.round(jobs / 7));

    // Mini Sparkline Component
    const MiniSparkline = ({ data, color }: { data: number[], color: string }) => {
        const max = Math.max(...data, 1);
        const min = Math.min(...data);
        const range = max - min || 1;

        return (
            <svg width="60" height="24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                <polyline
                    points={data.map((val, i) => `${(i / (data.length - 1)) * 60},${24 - ((val - min) / range) * 20}`).join(' ')}
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                />
                {data.map((val, i) => (
                    <circle
                        key={i}
                        cx={(i / (data.length - 1)) * 60}
                        cy={24 - ((val - min) / range) * 20}
                        r="2"
                        fill={color}
                    />
                ))}
            </svg>
        );
    };

    // Generate calendar heatmap data for last 56 days (8 weeks)
    const generateHeatmapData = () => {
        const heatmapData: { date: Date, count: number, jobs: any[], waterWashCount: number }[] = [];
        const daysToShow = 56; // 8 weeks

        for (let i = daysToShow - 1; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            date.setHours(0, 0, 0, 0);

            const dayJobs = allClosedJobs.filter(job => {
                const jobDate = new Date(job.endDate || job.committedDate);
                jobDate.setHours(0, 0, 0, 0);
                return jobDate.getTime() === date.getTime();
            });

            const dayWaterWash = waterWashRecords.filter((record) => {
                if (!record.createdOn) return false;
                const recordDate = new Date(record.createdOn);
                recordDate.setHours(0, 0, 0, 0);
                return recordDate.getTime() === date.getTime();
            });

            heatmapData.push({
                date,
                count: dayJobs.length,
                jobs: dayJobs,
                waterWashCount: dayWaterWash.length
            });
        }

        return heatmapData;
    };

    const heatmapData = generateHeatmapData();

    // Heatmap Calendar Component
    const HeatmapCalendar = () => {
        const maxCount = Math.max(...heatmapData.map(d => d.count), 1);
        const daysWithJobs = heatmapData.filter((d) => d.count > 0).length;
        const averageCount =
            daysWithJobs > 0
                ? heatmapData.reduce((sum, d) => sum + d.count, 0) / daysWithJobs
                : 0;

        const getIntensity = (count: number) => {
            if (count === 0) return 0;
            const ratio = count / maxCount;
            if (ratio <= 0.25) return 1;
            if (ratio <= 0.5) return 2;
            if (ratio <= 0.75) return 3;
            return 4;
        };

        const getColor = (count: number, intensity: number, waterWashCount: number = 0) => {
            // If no jobs but has water wash, return orange
            if (count === 0 && waterWashCount > 0) {
                return isDarkMode ? '#ff8c42' : '#ffa726';
            }

            if (intensity === 0) return isDarkMode ? '#1e1e2d' : '#f0f0f0';

            // Above average: Blue shades
            if (count > averageCount) {
                const blueColors = isDarkMode
                    ? ['#1e3a5f', '#2d5a8f', '#3d7ac7', '#009ef7']
                    : ['#cfe2ff', '#9ec5fe', '#6ea8fe', '#009ef7'];
                return blueColors[intensity - 1];
            }

            // At or below average: Green shades
            const greenColors = isDarkMode
                ? ['#1a3a2e', '#2d5f4c', '#40856a', '#50cd89']
                : ['#d4f4e0', '#a8e6c8', '#7dd9a8', '#50cd89'];
            return greenColors[intensity - 1];
        };

        // Organize data by day of week (rows) and week number (columns)
        const weeks: Array<Array<{ date: Date, count: number, jobs: any[], waterWashCount: number }>> = [];
        for (let i = 0; i < heatmapData.length; i += 7) {
            weeks.push(heatmapData.slice(i, i + 7));
        }

        // Reorganize to rows (days) × columns (weeks)
        const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const rows: Array<Array<{ date: Date, count: number, jobs: any[], waterWashCount: number }>> = dayLabels.map((_, dayIndex) =>
            weeks.map(week => week[dayIndex]).filter(Boolean)
        );

        return (
            <div>
                {/* Heatmap grid with day labels */}
                <div className="d-flex flex-column gap-1">
                    {rows.map((row, rowIdx) => (
                        <div key={rowIdx} className="d-flex gap-1 align-items-center">
                            {/* Day label */}
                            <div
                                className={`text-center fw-semibold ${isDarkMode ? 'text-white' : 'text-muted'}`}
                                style={{
                                    width: '18px',
                                    fontSize: '10px',
                                    opacity: 0.6,
                                    marginRight: '4px'
                                }}
                            >
                                {dayLabels[rowIdx]}
                            </div>

                            {/* Week cells */}
                            <div className="d-flex gap-1 flex-grow-1">
                                {row.map((day, colIdx) => {
                                    if (!day) return null;
                                    const intensity = getIntensity(day.count);
                                    const isAboveAverage = day.count > averageCount;
                                    const isWaterWashOnly = day.count === 0 && day.waterWashCount > 0;
                                    return (
                                        <div
                                            key={colIdx}
                                            className="rounded d-flex align-items-center justify-content-center"
                                            style={{
                                                flex: 1,
                                                minWidth: '24px',
                                                height: '20px',
                                                backgroundColor: getColor(day.count, intensity, day.waterWashCount),
                                                border: `1.5px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                position: 'relative'
                                            }}
                                            title={`${day.date.toLocaleDateString()}: ${
                                                isWaterWashOnly
                                                    ? `${day.waterWashCount} water wash (no jobs)`
                                                    : `${day.count} jobs${isAboveAverage ? ' (Above avg)' : ''}`
                                            }`}
                                            onClick={() => setSelectedDay({ date: day.date, jobs: day.jobs })}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.15)';
                                                e.currentTarget.style.boxShadow = isWaterWashOnly
                                                    ? '0 4px 12px rgba(255, 140, 66, 0.6)'
                                                    : isAboveAverage
                                                    ? '0 4px 12px rgba(0, 158, 247, 0.5)'
                                                    : '0 4px 12px rgba(80, 205, 137, 0.4)';
                                                e.currentTarget.style.zIndex = '10';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.boxShadow = 'none';
                                                e.currentTarget.style.zIndex = '1';
                                            }}
                                        >
                                            {day.count > 0 ? (
                                                <span
                                                    className="fw-bold"
                                                    style={{
                                                        fontSize: '9px',
                                                        color: intensity >= 3 ? '#ffffff' : (isDarkMode ? '#a1a5b7' : '#3f4254'),
                                                        lineHeight: 1,
                                                        textShadow: intensity >= 3 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
                                                    }}
                                                >
                                                    {day.count}
                                                </span>
                                            ) : isWaterWashOnly ? (
                                                <span
                                                    className="fw-bold"
                                                    style={{
                                                        fontSize: '9px',
                                                        color: '#ffffff',
                                                        lineHeight: 1,
                                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                    }}
                                                >
                                                    {day.waterWashCount}
                                                </span>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Legend */}
                <div className="d-flex flex-column gap-2 mt-4">
                    {/* Green legend - At/Below Average */}
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <span className={`fs-9 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ fontSize: '9px', opacity: 0.7, width: '60px', textAlign: 'right' }}>
                            At/Below Avg
                        </span>
                        {[1, 2, 3, 4].map((intensity) => (
                            <div
                                key={intensity}
                                className="rounded"
                                style={{
                                    width: '14px',
                                    height: '14px',
                                    backgroundColor: getColor(averageCount, intensity),
                                    border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`
                                }}
                            />
                        ))}
                    </div>
                    {/* Blue legend - Above Average */}
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <span className={`fs-9 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ fontSize: '9px', opacity: 0.7, width: '60px', textAlign: 'right' }}>
                            Above Avg
                        </span>
                        {[1, 2, 3, 4].map((intensity) => (
                            <div
                                key={intensity}
                                className="rounded"
                                style={{
                                    width: '14px',
                                    height: '14px',
                                    backgroundColor: getColor(averageCount + 1, intensity),
                                    border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`
                                }}
                            />
                        ))}
                    </div>
                    {/* Average indicator */}
                    <div className="text-center">
                        <span className={`fs-9 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ fontSize: '9px', opacity: 0.6 }}>
                            Avg: {averageCount.toFixed(1)} jobs/day
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // UI Components
    const InsightBadge = ({ icon, color, text, subtext }: any) => (
        <div className={`d-flex align-items-start p-3 rounded mb-2 h-100`} style={{ backgroundColor: isDarkMode ? `${color}15` : `${color}10`, border: `1px solid ${color}30`, minHeight: '100px' }}>
            <div className="symbol symbol-40px me-3 flex-shrink-0">
                <div className="symbol-label" style={{ backgroundColor: `${color}20` }}>
                    <i className={`bi ${icon} fs-2`} style={{ color: color }}></i>
                </div>
            </div>
            <div className="flex-grow-1 d-flex flex-column">
                <div className={`fw-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`} style={{ fontSize: '14px' }}>{text}</div>
                <div className={`fs-7 flex-grow-1 ${isDarkMode ? 'text-white' : 'text-gray-600'}`} style={{ opacity: isDarkMode ? 0.85 : 1, fontSize: '12px', lineHeight: '1.4' }}>{subtext}</div>
            </div>
        </div>
    );

    const StatCard = ({ title, value, icon, color, comparisons }: any) => (
        <div
            className="card h-100 shadow-sm"
            style={{
                backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
                border: `2px solid ${isDarkMode ? color + '40' : color + '30'}`,
                borderLeft: `4px solid ${color}`
            }}
        >
            <div className="card-body p-4">
                {/* Header with icon and main value */}
                <div className="d-flex align-items-center mb-3">
                    <div className="symbol symbol-50px me-4">
                        <div className="symbol-label" style={{ backgroundColor: `${color}20` }}>
                            <i className={`bi ${icon} fs-2x`} style={{ color: color }}></i>
                        </div>
                    </div>
                    <div className="flex-grow-1">
                        <div className={`fs-7 fw-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'}`} style={{ opacity: 0.85, letterSpacing: '0.5px' }}>
                            {title}
                        </div>
                        <div className={`fs-2x fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`}>{value}</div>
                    </div>
                </div>

                {/* Comparative Analysis */}
                {comparisons && (
                    <div className="pt-3" style={{ borderTop: `1px solid ${isDarkMode ? '#2d2d3f' : '#eff2f5'}` }}>
                        <div className="row g-2">
                            {comparisons.map((comp: any, idx: number) => (
                                <div className="col-6" key={idx}>
                                    <div
                                        className="p-2 rounded text-center"
                                        style={{
                                            backgroundColor: isDarkMode ? '#151521' : '#f8f9fa',
                                            border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`
                                        }}
                                    >
                                        <div className={`fs-8 fw-semibold ${isDarkMode ? 'text-white' : 'text-gray-600'}`} style={{ opacity: 0.7, fontSize: '10px' }}>
                                            {comp.label}
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center gap-1 mt-1">
                                            <span className={`fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`} style={{ fontSize: '14px' }}>
                                                {comp.value}
                                            </span>
                                            {comp.change !== undefined && comp.change !== 0 && (
                                                <span className={`badge badge-sm ${comp.change > 0 ? 'badge-light-success' : 'badge-light-danger'}`} style={{ fontSize: '9px', padding: '2px 4px' }}>
                                                    <i className={`bi bi-arrow-${comp.change > 0 ? 'up' : 'down'}`}></i>
                                                    {Math.abs(comp.change)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const CompactMetricCard = ({ title, value, icon, color, subMetrics }: any) => (
        <div
            className="rounded-3 p-3 h-100"
            style={{
                backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
                border: `2px solid ${isDarkMode ? color + '40' : color + '30'}`,
                borderLeft: `4px solid ${color}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
        >
            <div className="d-flex align-items-center mb-3">
                <div className="symbol symbol-40px me-3">
                    <div className="symbol-label" style={{ backgroundColor: `${color}20` }}>
                        <i className={`bi ${icon} fs-2`} style={{ color: color }}></i>
                    </div>
                </div>
                <div>
                    <div className={`fs-8 fw-semibold ${isDarkMode ? 'text-white' : 'text-gray-600'}`} style={{ opacity: 0.75, fontSize: '11px' }}>
                        {title}
                    </div>
                    <div className={`fs-1 fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`}>{value}</div>
                </div>
            </div>
            {subMetrics && (
                <div className="d-flex flex-wrap gap-2">
                    {subMetrics.map((metric: any, idx: number) => (
                        <div
                            key={idx}
                            className="flex-fill text-center px-2 py-1 rounded"
                            style={{
                                backgroundColor: isDarkMode ? '#151521' : '#f8f9fa',
                                border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`,
                                minWidth: '70px'
                            }}
                        >
                            <div className={`fs-9 ${isDarkMode ? 'text-white' : 'text-gray-600'}`} style={{ opacity: 0.7, fontSize: '9px' }}>
                                {metric.label}
                            </div>
                            <div className={`fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`} style={{ fontSize: '13px' }}>
                                {metric.value}
                                {metric.change !== undefined && metric.change !== 0 && (
                                    <i className={`bi bi-arrow-${metric.change > 0 ? 'up' : 'down'} ms-1 ${metric.change > 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '10px' }}></i>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="card mb-5" style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff' }}>
            {/* Header */}
            <div className="card-header border-0 pt-6 pb-0">
                <h3 className={`card-title align-items-start flex-column`}>
                    <span className={`card-label fw-bolder fs-2 ${isDarkMode ? 'text-white' : 'text-dark'}`}>
                        <i className="bi bi-lightbulb me-2 text-warning"></i>
                        Smart Operational Insights
                    </span>
                    <span className={`mt-1 fw-bold fs-7 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.7 : 1 }}>
                        {role === 'manager' ? 'Team performance and operational metrics' : 'Daily operations and efficiency tracking'}
                    </span>
                </h3>
                <div className="card-toolbar">
                    <span className="badge badge-light-success fw-bolder px-4 py-2">
                        Updated Recently
                    </span>
                </div>
            </div>

            <div className="card-body py-6">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                        <span className="spinner-border text-primary"></span>
                    </div>
                ) : (
                    <>
                        {/* Row 2: Activity Patterns and Smart Recommendations */}
                        <div className="row g-4">
                            {/* Activity Patterns */}
                            <div className="col-lg-4 col-md-12">
                                <h4 className={`fs-6 fw-bold mb-3 text-uppercase ls-1 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>
                                    Activity Patterns
                                </h4>

                                {/* Compact Info Cards */}
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <div
                                            className="rounded-3 p-3 h-100"
                                            style={{
                                                background: isDarkMode
                                                    ? 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)'
                                                    : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                                border: isDarkMode ? '1px solid #4a5568' : '1px solid #d1d5db',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                            }}
                                        >
                                            <div className="text-center mb-2">
                                                <span className="fs-1">🔥</span>
                                            </div>
                                            <div className={`text-center fw-bold mb-1 ${isDarkMode ? 'text-white' : 'text-dark'}`} style={{ fontSize: '13px' }}>
                                                Busiest Day
                                            </div>
                                            <div className={`text-center ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ fontSize: '11px', opacity: 0.9 }}>
                                                <div><strong>{dayStats.bestDay}s</strong></div>
                                                <div className="mt-1">{dayStats.bestCount} jobs</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-6">
                                        <div
                                            className="rounded-3 p-3 h-100"
                                            style={{
                                                background: isDarkMode
                                                    ? 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)'
                                                    : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                                border: isDarkMode ? '1px solid #4a5568' : '1px solid #d1d5db',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                            }}
                                        >
                                            <div className="text-center mb-2">
                                                <span className="fs-1">🐢</span>
                                            </div>
                                            <div className={`text-center fw-bold mb-1 ${isDarkMode ? 'text-white' : 'text-dark'}`} style={{ fontSize: '13px' }}>
                                                Quietest Day
                                            </div>
                                            <div className={`text-center ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ fontSize: '11px', opacity: 0.9 }}>
                                                <div><strong>{dayStats.slowestDay}s</strong></div>
                                                <div className="mt-1">{dayStats.slowestCount} jobs</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Month Progress */}
                                <div className="card border-0 shadow-none" style={{ backgroundColor: isDarkMode ? '#151521' : '#f8f9fa' }}>
                                    <div className="card-body p-4">
                                        <div className={`fw-semibold fs-7 mb-3 text-uppercase ${isDarkMode ? 'text-white' : 'text-gray-500'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>
                                            Monthly Target
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className={`fs-6 fw-bold ${isDarkMode ? 'text-white' : 'text-gray-700'}`} style={{ opacity: isDarkMode ? 0.8 : 1 }}>
                                                Job Count
                                            </span>
                                            <span className={`fs-6 fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`}>
                                                {predictedMonthJobCount}
                                            </span>
                                        </div>
                                        <div className="progress h-6px w-100 bg-light-primary mb-1">
                                            <div className="progress-bar bg-primary" role="progressbar"
                                                 style={{ width: `${(currentMonthJobCount / predictedMonthJobCount) * 100}%` }}></div>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span className={`fs-8 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.7 : 1 }}>
                                                Current: {currentMonthJobCount}
                                            </span>
                                            <span className="fs-8 text-primary fw-bold">Projected</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Smart Recommendations */}
                            <div className="col-lg-8 col-md-12">
                                <h4 className={`fs-6 fw-bold mb-3 text-uppercase ls-1 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>
                                    Smart Recommendations
                                    <span className="ms-2" style={{ fontSize: '10px', opacity: 0.6 }}>(6 of 20)</span>
                                </h4>
                                <div className="row g-2">
                                    {operationalRecommendations.map((rec, idx) => (
                                        <div className="col-lg-4 col-md-6 col-12" key={idx}>
                                            <InsightBadge
                                                icon={rec.icon}
                                                color={rec.color}
                                                text={rec.text}
                                                subtext={rec.subtext}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Day Details Modal */}
            {selectedDay && (
                <div
                    className="modal fade show"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)' }}
                    onClick={() => setSelectedDay(null)}
                >
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div
                            className="modal-content"
                            style={{
                                backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
                                border: isDarkMode ? '1px solid #2d2d3f' : '1px solid #e4e6ef'
                            }}
                        >
                            <div
                                className="modal-header border-0 pb-0"
                                style={{ borderBottom: isDarkMode ? '1px solid #2d2d3f' : '1px solid #e4e6ef' }}
                            >
                                <h5 className={`modal-title fw-bolder ${isDarkMode ? 'text-white' : 'text-dark'}`}>
                                    <i className="bi bi-calendar-day me-2" style={{ color: selectedDay.jobs.length > 0 ? '#50cd89' : '#a1a5b7' }}></i>
                                    {selectedDay.date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                </h5>
                                <button
                                    type="button"
                                    className={`btn-close ${isDarkMode ? 'btn-close-white' : ''}`}
                                    onClick={() => setSelectedDay(null)}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body py-5">
                                {selectedDay.jobs.length > 0 || (() => {
                                    const allJobs = [...allActiveJobs, ...allClosedJobs];
                                    const jobsCreatedOnDay = allJobs.filter((job) => {
                                        if (!job.createdOn) return false;
                                        const createdDate = new Date(job.createdOn);
                                        createdDate.setHours(0, 0, 0, 0);
                                        return createdDate.getTime() === selectedDay.date.getTime();
                                    });
                                    return jobsCreatedOnDay.length > 0;
                                })() || (() => {
                                    const waterWashOnDay = waterWashRecords.filter((record) => {
                                        if (!record.createdOn) return false;
                                        const createdDate = new Date(record.createdOn);
                                        createdDate.setHours(0, 0, 0, 0);
                                        return createdDate.getTime() === selectedDay.date.getTime();
                                    });
                                    return waterWashOnDay.length > 0;
                                })() ? (
                                    <div className="row g-4">
                                        {/* Jobs Completed */}
                                        <div className="col-6">
                                            <div
                                                className="text-center p-4 rounded-3"
                                                style={{
                                                    backgroundColor: isDarkMode ? '#1c1c28' : '#f5f8fa',
                                                    border: `2px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`
                                                }}
                                            >
                                                <i className="bi bi-check-circle-fill fs-2x mb-3" style={{ color: '#50cd89', display: 'block' }}></i>
                                                <div className={`fs-2x fw-bolder ${isDarkMode ? 'text-white' : 'text-dark'} mb-2`}>
                                                    {selectedDay.jobs.length}
                                                </div>
                                                <div className="text-muted fs-7 fw-bold text-uppercase">Jobs Closed</div>
                                            </div>
                                        </div>

                                        {/* Total Treatments */}
                                        <div className="col-6">
                                            <div
                                                className="text-center p-4 rounded-3"
                                                style={{
                                                    backgroundColor: isDarkMode ? '#1c1c28' : '#f5f8fa',
                                                    border: `2px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`
                                                }}
                                            >
                                                <i className="bi bi-clipboard-check fs-2x mb-3" style={{ color: '#009ef7', display: 'block' }}></i>
                                                <div className={`fs-2x fw-bolder ${isDarkMode ? 'text-white' : 'text-dark'} mb-2`}>
                                                    {selectedDay.jobs.reduce((sum: number, job: any) => sum + (job.treatmentCount || 0), 0)}
                                                </div>
                                                <div className="text-muted fs-7 fw-bold text-uppercase">Total Treatments</div>
                                            </div>
                                        </div>

                                        {/* Jobs Created */}
                                        <div className="col-6">
                                            <div
                                                className="text-center p-4 rounded-3"
                                                style={{
                                                    backgroundColor: isDarkMode ? '#1c1c28' : '#f5f8fa',
                                                    border: `2px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`
                                                }}
                                            >
                                                <i className="bi bi-plus-circle-fill fs-2x mb-3" style={{ color: '#7239ea', display: 'block' }}></i>
                                                <div className={`fs-2x fw-bolder ${isDarkMode ? 'text-white' : 'text-dark'} mb-2`}>
                                                    {(() => {
                                                        const allJobs = [...allActiveJobs, ...allClosedJobs];
                                                        return allJobs.filter((job) => {
                                                            if (!job.createdOn) return false;
                                                            const createdDate = new Date(job.createdOn);
                                                            createdDate.setHours(0, 0, 0, 0);
                                                            return createdDate.getTime() === selectedDay.date.getTime();
                                                        }).length;
                                                    })()}
                                                </div>
                                                <div className="text-muted fs-7 fw-bold text-uppercase">Jobs Created</div>
                                            </div>
                                        </div>

                                        {/* Water Wash Created */}
                                        <div className="col-6">
                                            <div
                                                className="text-center p-4 rounded-3"
                                                style={{
                                                    backgroundColor: isDarkMode ? '#1c1c28' : '#f5f8fa',
                                                    border: `2px solid ${isDarkMode ? '#2d2d3f' : '#e4e6ef'}`
                                                }}
                                            >
                                                <i className="bi bi-droplet-fill fs-2x mb-3" style={{ color: '#009ef7', display: 'block' }}></i>
                                                <div className={`fs-2x fw-bolder ${isDarkMode ? 'text-white' : 'text-dark'} mb-2`}>
                                                    {waterWashRecords.filter((record) => {
                                                        if (!record.createdOn) return false;
                                                        const createdDate = new Date(record.createdOn);
                                                        createdDate.setHours(0, 0, 0, 0);
                                                        return createdDate.getTime() === selectedDay.date.getTime();
                                                    }).length}
                                                </div>
                                                <div className="text-muted fs-7 fw-bold text-uppercase">Water Wash Created</div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="bi bi-inbox fs-3x text-muted mb-3" style={{ display: 'block' }}></i>
                                        <div className={`${isDarkMode ? 'text-gray-400' : 'text-muted'}`}>No jobs completed on this day</div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-light-primary btn-sm"
                                    onClick={() => setSelectedDay(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
